import { memo } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { Button, Icons, Tooltip } from '@/js/libs/ui/src/V2';

import { ROUTER } from 'src/AppRouter';
import { useRecover } from 'src/hooks/useRecover';
import { useNavigationContext } from '../../context';
import { MailIconIndicator, MailIconWrapper } from './styled';

const getTooltip = (unreadCommentsCount: number, unreadSystemNotificationsCount: number, t: TFunction) => {
  if (unreadSystemNotificationsCount > 0) {
    return t('common:UNREAD_SYSTEM_NOTIFICATIONS_COUNT_TOOLTIP', { count: unreadSystemNotificationsCount });
  }
  if (unreadCommentsCount > 0) {
    return t('common:UNREAD_MESSAGES_COUNT_TOOLTIP', { count: unreadCommentsCount });
  }
  return undefined;
};

export const NotificationsButton = memo(function NotificationsButton() {
  const [t] = useTranslation(['backend', 'common']);
  const { colors } = useTheme();

  const { unreadCommentsCountRequest, unreadSystemNotificationsCountRequest } = useNavigationContext();

  const recoverSystemNotifications = useRecover(unreadSystemNotificationsCountRequest);
  const recoverComments = useRecover(unreadCommentsCountRequest);

  const unreadSystemNotificationsCount = useSubscribe(unreadSystemNotificationsCountRequest.data$, (data) => data?.data ?? 0);
  const unreadCommentsCount = useSubscribe(unreadCommentsCountRequest.data$, (data) => data?.data ?? 0);

  const onIconClick = useCallbackRef(() => {
    if (unreadCommentsCount > 0 && unreadSystemNotificationsCount === 0) {
      ROUTER['/notifications/comments'].path.navigate();
    } else {
      ROUTER['/notifications/system'].path.navigate();
    }
  });

  if (recoverSystemNotifications) {
    return recoverSystemNotifications;
  }

  if (recoverComments) {
    return recoverComments;
  }

  return (
    <Tooltip ContainerComponent={MailIconWrapper} tooltip={getTooltip(unreadCommentsCount, unreadSystemNotificationsCount, t)}>
      <Button iconOnly ghost onClick={onIconClick} dataTestId='mail-button'>
        {(unreadCommentsCount > 0 || unreadSystemNotificationsCount > 0) && (
          <MailIconIndicator positive={unreadCommentsCount > 0} warning={unreadSystemNotificationsCount > 0} />
        )}
        <Icons.Mail color={colors.grey100_1} />
      </Button>
    </Tooltip>
  );
});
